import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Sidebar } from "@/components/Sidebar";
import AIChat from "@/components/AIChat";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import {
  Warehouse, Plus, Search, Car, Clock, TrendingDown, AlertTriangle, Eye, Users,
  MessageSquare, Brain, Bot, ChevronRight, Sparkles, DollarSign, Calendar
} from "lucide-react";

export default function InventoryPage() {
  const [vehicles, setVehicles] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showChat, setShowChat] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [showAddVehicle, setShowAddVehicle] = useState(false);
  const [newVehicle, setNewVehicle] = useState({
    make: "", model: "", year: 2024, price: 0, mileage: 0, condition: "new"
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [vehiclesRes, workersRes] = await Promise.all([
        axios.get(`${API}/vehicles`),
        axios.get(`${API}/digital-workers/inventory`)
      ]);
      setVehicles(vehiclesRes.data);
      setWorkers(workersRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddVehicle = async () => {
    try {
      await axios.post(`${API}/vehicles`, newVehicle);
      setShowAddVehicle(false);
      setNewVehicle({ make: "", model: "", year: 2024, price: 0, mileage: 0, condition: "new" });
      fetchData();
    } catch (error) {
      console.error("Error adding vehicle:", error);
    }
  };

  const getDaysColor = (days) => {
    if (days >= 60) return "text-red-500 bg-red-500/10";
    if (days >= 45) return "text-amber-500 bg-amber-500/10";
    if (days >= 30) return "text-yellow-500 bg-yellow-500/10";
    return "text-emerald-500 bg-emerald-500/10";
  };

  const agingVehicles = vehicles.filter(v => v.days_on_lot >= 45);
  const newInventory = vehicles.filter(v => v.condition === "new");
  const usedInventory = vehicles.filter(v => v.condition === "used");
  const totalValue = vehicles.reduce((a, v) => a + (v.price || 0), 0);

  const filteredVehicles = vehicles.filter(vehicle => {
    const searchStr = `${vehicle.year} ${vehicle.make} ${vehicle.model} ${vehicle.stock_number}`.toLowerCase();
    const matchesSearch = searchStr.includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === "all" || vehicle.status === filterStatus || 
                         (filterStatus === "aging" && vehicle.days_on_lot >= 45);
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <div className="dashboard-content">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="font-display text-3xl font-bold text-foreground flex items-center gap-3" data-testid="inventory-title">
              <Warehouse className="w-8 h-8 text-cyan-500" />
              Inventory
            </h1>
            <p className="text-muted-foreground">AI-powered inventory aging and acquisition management</p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setShowChat(true)} className="gap-2">
              <Brain className="w-4 h-4" /> AI Assistant
            </Button>
            <Dialog open={showAddVehicle} onOpenChange={setShowAddVehicle}>
              <DialogTrigger asChild>
                <Button className="btn-ai gap-2" data-testid="add-vehicle-btn">
                  <Plus className="w-4 h-4" /> Add Vehicle
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add New Vehicle</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <Input placeholder="Make" value={newVehicle.make} onChange={(e) => setNewVehicle({...newVehicle, make: e.target.value})} />
                    <Input placeholder="Model" value={newVehicle.model} onChange={(e) => setNewVehicle({...newVehicle, model: e.target.value})} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <Input type="number" placeholder="Year" value={newVehicle.year} onChange={(e) => setNewVehicle({...newVehicle, year: Number(e.target.value)})} />
                    <Select value={newVehicle.condition} onValueChange={(v) => setNewVehicle({...newVehicle, condition: v})}>
                      <SelectTrigger><SelectValue placeholder="Condition" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="new">New</SelectItem>
                        <SelectItem value="used">Used</SelectItem>
                        <SelectItem value="certified">Certified Pre-Owned</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <Input type="number" placeholder="Price ($)" value={newVehicle.price || ""} onChange={(e) => setNewVehicle({...newVehicle, price: Number(e.target.value)})} />
                    <Input type="number" placeholder="Mileage" value={newVehicle.mileage || ""} onChange={(e) => setNewVehicle({...newVehicle, mileage: Number(e.target.value)})} />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowAddVehicle(false)}>Cancel</Button>
                  <Button className="btn-ai" onClick={handleAddVehicle}>Add Vehicle</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <Tabs defaultValue="vehicles" className="space-y-6">
          <TabsList>
            <TabsTrigger value="vehicles" className="gap-2"><Car className="w-4 h-4" /> All Vehicles</TabsTrigger>
            <TabsTrigger value="aging" className="gap-2"><Clock className="w-4 h-4" /> Aging Units</TabsTrigger>
            <TabsTrigger value="workers" className="gap-2"><Bot className="w-4 h-4" /> Digital Workers</TabsTrigger>
          </TabsList>

          {/* All Vehicles Tab */}
          <TabsContent value="vehicles" className="space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { label: "Total Inventory", value: vehicles.length, icon: Car, color: "text-cyan-500" },
                { label: "New Vehicles", value: newInventory.length, icon: Sparkles, color: "text-emerald-500" },
                { label: "Used Vehicles", value: usedInventory.length, icon: Calendar, color: "text-blue-500" },
                { label: "Total Value", value: `$${(totalValue / 1000000).toFixed(1)}M`, icon: DollarSign, color: "text-amber-500" },
              ].map((stat, idx) => (
                <Card key={idx}>
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg ${stat.color.replace('text-', 'bg-')}/10 flex items-center justify-center`}>
                      <stat.icon className={`w-5 h-5 ${stat.color}`} />
                    </div>
                    <div>
                      <div className="text-2xl font-bold">{stat.value}</div>
                      <div className="text-xs text-muted-foreground">{stat.label}</div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Search and Filter */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input placeholder="Search vehicles..." className="pl-10" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
              </div>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-48"><SelectValue placeholder="All Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="available">Available</SelectItem>
                  <SelectItem value="sold">Sold</SelectItem>
                  <SelectItem value="aging">Aging (45+ days)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Vehicles Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {loading ? (
                <div className="col-span-full text-center py-8 text-muted-foreground">Loading...</div>
              ) : filteredVehicles.length === 0 ? (
                <div className="col-span-full text-center py-8 text-muted-foreground">No vehicles found</div>
              ) : (
                filteredVehicles.map((vehicle) => (
                  <Card key={vehicle.id} className="hover:border-cyan-500/30 transition-all" data-testid={`vehicle-${vehicle.id}`}>
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="font-semibold text-lg text-foreground">
                            {vehicle.year} {vehicle.make} {vehicle.model}
                          </div>
                          <div className="text-sm text-muted-foreground">{vehicle.stock_number}</div>
                        </div>
                        <Badge className={getDaysColor(vehicle.days_on_lot)}>
                          {vehicle.days_on_lot} days
                        </Badge>
                      </div>

                      <div className="grid grid-cols-2 gap-3 text-sm mb-4">
                        <div className="flex items-center gap-2">
                          <DollarSign className="w-4 h-4 text-muted-foreground" />
                          <span className="font-semibold text-foreground">${vehicle.price?.toLocaleString()}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Car className="w-4 h-4 text-muted-foreground" />
                          <span>{vehicle.mileage?.toLocaleString()} mi</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Eye className="w-4 h-4 text-muted-foreground" />
                          <span>{vehicle.srp_views || 0} views</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-muted-foreground" />
                          <span>{vehicle.leads_generated || 0} leads</span>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 mb-3">
                        <Badge variant="outline">{vehicle.condition}</Badge>
                        <Badge className={vehicle.status === "available" ? "bg-emerald-500/10 text-emerald-500" : "bg-slate-500/10 text-slate-400"}>
                          {vehicle.status}
                        </Badge>
                      </div>

                      {vehicle.ai_action_needed && (
                        <div className="p-3 rounded-lg bg-amber-500/5 border border-amber-500/20 flex items-start gap-2">
                          <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5" />
                          <div>
                            <div className="text-sm font-medium text-amber-500">{vehicle.ai_action_needed}</div>
                            {vehicle.ai_pricing_recommendation && (
                              <div className="text-xs text-amber-400 mt-1">{vehicle.ai_pricing_recommendation}</div>
                            )}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Aging Units Tab */}
          <TabsContent value="aging" className="space-y-6">
            <Card className="border-amber-500/30">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-amber-500">
                  <Clock className="w-5 h-5" />
                  Aging Inventory Alert - {agingVehicles.length} Units
                </CardTitle>
                <CardDescription>Vehicles on lot 45+ days requiring action</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {agingVehicles.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">No aging units! All inventory is turning well.</div>
                ) : (
                  agingVehicles.map((vehicle) => (
                    <div key={vehicle.id} className="p-4 rounded-lg border border-border hover:border-amber-500/30 transition-all">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="font-semibold text-foreground">
                            {vehicle.year} {vehicle.make} {vehicle.model}
                          </div>
                          <div className="text-sm text-muted-foreground">{vehicle.stock_number} • ${vehicle.price?.toLocaleString()}</div>
                        </div>
                        <Badge className={getDaysColor(vehicle.days_on_lot)}>
                          <Clock className="w-3 h-3 mr-1" />
                          {vehicle.days_on_lot} days
                        </Badge>
                      </div>

                      <div className="grid grid-cols-3 gap-4 text-center text-sm mb-4">
                        <div>
                          <div className="text-muted-foreground">SRP Views</div>
                          <div className="font-semibold">{vehicle.srp_views || 0}</div>
                        </div>
                        <div>
                          <div className="text-muted-foreground">Leads</div>
                          <div className="font-semibold">{vehicle.leads_generated || 0}</div>
                        </div>
                        <div>
                          <div className="text-muted-foreground">View/Lead Rate</div>
                          <div className="font-semibold">
                            {vehicle.leads_generated && vehicle.srp_views ? 
                              `${((vehicle.leads_generated / vehicle.srp_views) * 100).toFixed(1)}%` : '0%'}
                          </div>
                        </div>
                      </div>

                      {vehicle.ai_action_needed && (
                        <div className="p-3 rounded-lg bg-amber-500/5 border border-amber-500/20">
                          <div className="flex items-center gap-2 text-amber-500 font-medium mb-1">
                            <Sparkles className="w-4 h-4" /> AI Recommendation: {vehicle.ai_action_needed}
                          </div>
                          {vehicle.ai_pricing_recommendation && (
                            <div className="text-sm text-amber-400">{vehicle.ai_pricing_recommendation}</div>
                          )}
                        </div>
                      )}

                      <div className="flex gap-2 mt-3">
                        <Button size="sm" variant="outline">Update Price</Button>
                        <Button size="sm" variant="outline">Refresh Photos</Button>
                        <Button size="sm" variant="outline">Create Campaign</Button>
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Digital Workers Tab */}
          <TabsContent value="workers" className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              {workers.map((worker) => (
                <Card key={worker.id} className="hover:border-cyan-500/30 transition-all group" data-testid={`worker-${worker.id}`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Bot className="w-6 h-6 text-cyan-500" />
                      </div>
                      <Badge className="bg-emerald-500/10 text-emerald-500">{worker.status}</Badge>
                    </div>
                    <CardTitle className="text-lg mt-3">{worker.name}</CardTitle>
                    <CardDescription className="text-cyan-400">{worker.subtitle}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-sm text-muted-foreground">{worker.description}</p>
                    
                    <div className="space-y-2">
                      {worker.features?.map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-xs text-muted-foreground">
                          <ChevronRight className="w-3 h-3 text-cyan-500" />
                          {feature}
                        </div>
                      ))}
                    </div>

                    {worker.metrics && (
                      <div className="grid grid-cols-3 gap-2 pt-3 border-t border-border">
                        {Object.entries(worker.metrics).map(([key, value]) => (
                          <div key={key} className="text-center">
                            <div className="text-sm font-bold text-foreground">{value}</div>
                            <div className="text-xs text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <AIChat isOpen={showChat} onClose={() => setShowChat(false)} vertical="inventory" />
      
      {!showChat && (
        <button onClick={() => setShowChat(true)} className="fixed bottom-6 right-6 w-14 h-14 rounded-full btn-ai flex items-center justify-center shadow-lg hover:scale-110 transition-transform" style={{ zIndex: 9998 }} data-testid="chat-fab">
          <MessageSquare className="w-6 h-6" />
        </button>
      )}
    </div>
  );
}
